import React from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartLine,
  faRocket,
  faShieldAlt,
  faLightbulb,
  faArrowRight
} from '@fortawesome/free-solid-svg-icons';

interface Solution {
  icon: any;
  title: string;
  description: string;
  benefits: string[];
  gradient: string;
}

const solutions: Solution[] = [
  {
    icon: faRocket,
    title: 'Launch Strategy',
    description: 'Comprehensive go-to-market strategies for Web3 projects',
    benefits: [
      'Market positioning',
      'Community building',
      'Token economics design',
      'Launch timeline optimization'
    ],
    gradient: 'from-persian to-persian-600'
  },
  {
    icon: faChartLine,
    title: 'Growth Marketing',
    description: 'Data-driven approaches to scale your blockchain project',
    benefits: [
      'Performance analytics',
      'User acquisition',
      'Retention strategies',
      'Revenue optimization'
    ],
    gradient: 'from-verdigris to-verdigris-600'
  },
  {
    icon: faShieldAlt,
    title: 'Brand Protection',
    description: 'Safeguard your reputation in the decentralized ecosystem',
    benefits: [
      'Crisis management',
      'Community moderation',
      'Security audits communication',
      'Trust building'
    ],
    gradient: 'from-vanilla to-vanilla-600'
  },
  {
    icon: faLightbulb,
    title: 'Innovation Consulting',
    description: 'Stay ahead with cutting-edge Web3 marketing strategies',
    benefits: [
      'Trend analysis',
      'Competitive research',
      'Technology integration',
      'Future-proofing'
    ],
    gradient: 'from-linen-400 to-linen-600'
  }
];

const Solutions: React.FC = () => {
  return (
    <section id="solutions" className="py-20 relative overflow-hidden">
      <div className="max-container section-padding relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ scale: 0 }}
            whileInView={{ scale: 1 }}
            transition={{ type: 'spring' }}
            className="inline-block glass px-4 py-2 rounded-full mb-4"
          >
            <span className="text-sm text-verdigris">Solutions</span>
          </motion.div>
          <h2 className="heading-2 text-linen mb-4">
            Tailored <span className="gradient-text">Marketing Solutions</span>
          </h2>
          <p className="body-text max-w-2xl mx-auto">
            We provide end-to-end marketing solutions designed specifically for the unique
            challenges and opportunities in the Web3 space.
          </p>
        </motion.div>

        {/* Solutions Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {solutions.map((solution, index) => (
            <motion.div
              key={solution.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -5 }}
              className="glass rounded-xl p-8 group hover:bg-verdigris/5 transition-all duration-300"
            >
              <div className="flex items-start space-x-6">
                {/* Icon */}
                <motion.div
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.5 }}
                  className={`w-16 h-16 bg-gradient-to-br ${solution.gradient} rounded-xl flex items-center justify-center flex-shrink-0`}
                >
                  <FontAwesomeIcon icon={solution.icon} className="text-white text-2xl" />
                </motion.div>

                {/* Content */}
                <div className="flex-1">
                  <h3 className="text-2xl font-semibold text-linen mb-3">{solution.title}</h3>
                  <p className="text-linen-400 mb-4">{solution.description}</p>

                  {/* Benefits */}
                  <ul className="space-y-2 mb-4">
                    {solution.benefits.map((benefit, i) => (
                      <motion.li
                        key={i}
                        initial={{ opacity: 0, x: -10 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 + i * 0.05 }}
                        className="flex items-center text-sm text-linen-300"
                      >
                        <span className="w-1.5 h-1.5 bg-verdigris rounded-full mr-2"></span>
                        {benefit}
                      </motion.li>
                    ))}
                  </ul>

                  {/* Learn More Link */}
                  <motion.a
                    href="#contact"
                    className="inline-flex items-center text-verdigris hover:text-verdigris-400 transition-colors group"
                    whileHover={{ x: 5 }}
                  >
                    <span className="mr-2">Learn More</span>
                    <FontAwesomeIcon
                      icon={faArrowRight}
                      className="transition-transform group-hover:translate-x-1"
                    />
                  </motion.a>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <p className="text-linen-400 mb-6">
            Ready to transform your Web3 marketing strategy?
          </p>
          <motion.a
            href="#contact"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="btn-primary inline-flex items-center"
          >
            Start Your Journey
            <FontAwesomeIcon
              icon={faArrowRight}
              className="ml-2"
            />
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

export default Solutions;